import { type FC, type PropsWithChildren, type ReactNode } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { Price } from '@eneco/flows/components';
import { Box, Divider, Stack, Stretch, Text, Badge } from '@eneco/sparky';

import { Price as PriceType } from '../../../shared/types';

export interface ProductCardContentProps {
  /** Prefix for title/price element ids (from `useId()` in the parent card). */
  id: string;
  title?: string;
  ribbon?: ReactNode;
  touRibbon?: ReactNode;
  price?: PriceType;
  tag?: string;
  usps?: string[];
  /** Optional leading content in the title row (e.g. RadioCard.Indicator). */
  titlePrefix?: ReactNode;
}

const OfferCardRibbons: FC<Pick<ProductCardContentProps, 'ribbon' | 'touRibbon'>> = ({ ribbon, touRibbon }) => (
  <>
    {ribbon && (
      <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
        {ribbon}
      </Box>
    )}
    {touRibbon && !ribbon && (
      <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
        {touRibbon}
      </Box>
    )}
  </>
);

export const ProductCardContent: FC<PropsWithChildren<ProductCardContentProps>> = ({
  id,
  title,
  ribbon,
  touRibbon,
  price,
  tag,
  usps,
  titlePrefix,
  children,
}) => (
  <Stretch>
    <Stack direction="column">
      <Stack.Item grow={true}>
        <OfferCardRibbons ribbon={ribbon} touRibbon={touRibbon} />

        <Box padding="6">
          <Stack gap="2">
            <Stack direction="row" alignX="justify" gap="2">
              {(titlePrefix || title) && (
                <Stack direction="row" gap="2" alignY="start">
                  {titlePrefix && <Stack.Item shrink={false}>{titlePrefix}</Stack.Item>}
                  {title && (
                    <Text id={`${id}-label`} weight="bold">
                      {title}
                    </Text>
                  )}
                </Stack>
              )}

              {price && (
                <Stack.Item shrink={{ initial: true, lg: false }}>
                  <Text align="right">
                    <Price id={`${id}-price`} suffix={price.period}>
                      {price.value}
                    </Price>
                  </Text>
                </Stack.Item>
              )}
            </Stack>

            {tag && (
              <Stack.Item>
                <Badge color="alpha">{tag}</Badge>
              </Stack.Item>
            )}

            {usps && (
              <Checklist>
                {usps.map(usp => (
                  <Checklist.Item key={usp} gap="2">
                    <Stack>
                      <RichText html={usp} />
                    </Stack>
                  </Checklist.Item>
                ))}
              </Checklist>
            )}
          </Stack>
        </Box>
      </Stack.Item>

      {children && (
        <Stack.Item>
          <Divider />
          {children}
        </Stack.Item>
      )}
    </Stack>
  </Stretch>
);
